import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Repeat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { QuickCampaignButton } from "@/components/outreach/QuickCampaign";
import { MessageDialog } from "@/components/si/MessageDialog";
import { TalentContact } from "@/components/si/TalentContact";
import { api, errMsg } from "@/lib/api";
import { SI, useSI } from "@/lib/sourcing";
import { contactOf } from "@/lib/talentProfile";

// §103 Add a candidate to an existing email sequence.
const SequenceDialog = ({ c, pid, email, recordSource, open, onClose }) => {
  const q = useSI(open ? "/admin/rcc/campaigns?status=active&limit=50" : null);
  const [id, setId] = useState("");
  const [busy, setBusy] = useState(false);
  const items = q.data?.items || [];
  const go = async () => {
    setBusy(true);
    try { await api.post(`${SI}/candidates/${c.id}/sequence`, { campaign_id: id, source: recordSource, project_id: pid, email }); toast.success(`${c.full_name || "Candidate"} added to the sequence`); onClose(); }
    catch (e) { toast.error(e.response?.status === 404 ? "Adding candidates to sequences isn't live on the server yet (spec §103)." : errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#27272A] bg-[#0f0f12]" data-testid="cand-seq-dialog">
        <DialogTitle className="font-display text-zinc-50">Add {c.full_name || "candidate"} to a sequence</DialogTitle>
        <p className="text-xs text-zinc-400">Emails go to <span className="text-zinc-200">{email || "their best email"}</span> from your connected mailbox. A reply stops the sequence.</p>
        {q.loading && !q.data ? <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" /> : !items.length ? <p className="text-xs text-zinc-500" data-testid="cand-seq-empty">No active sequences yet. Use Email campaign to build one for this candidate.</p>
          : <select value={id} onChange={(e) => setId(e.target.value)} className="h-9 w-full rounded-md border border-[#27272A] bg-[#121215] px-2 text-sm text-zinc-200" data-testid="cand-seq-select"><option value="">Choose a sequence…</option>{items.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>}
        <Button onClick={go} disabled={!id || busy} className="bg-[#D4AF37] text-black hover:bg-[#F3C649]" data-testid="cand-seq-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Add to sequence</Button>
      </DialogContent>
    </Dialog>
  );
};

// One-off email, sequence or campaign for any candidate.
export const CandidateActions = ({ c, pid, email, recordSource, onEmail, campaign }) => {
  const [seq, setSeq] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="cand-actions">
      <Button size="sm" onClick={() => onEmail(email)} disabled={!email} title={email ? `Email ${email}` : "Add an email first"} className="h-8 bg-[#D4AF37] text-black hover:bg-[#F3C649]" data-testid="cand-email"><Mail className="h-3.5 w-3.5" />Email</Button>
      <Button size="sm" variant="outline" onClick={() => setSeq(true)} disabled={!email} className="h-8 border-[#D4AF37]/40 text-[#F3C649]" data-testid="cand-sequence"><Repeat className="h-3.5 w-3.5" />Add to sequence</Button>
      <QuickCampaignButton source={campaign?.source || "candidate"} ids={campaign?.ids || [c.id]} recordSource={campaign ? undefined : recordSource} label={c.full_name} testid="cand-campaign" />
      {!email && <span className="text-[10px] text-zinc-500" data-testid="cand-no-email">Add or pick an email to send.</span>}
      <SequenceDialog c={c} pid={pid} email={email} recordSource={recordSource} open={seq} onClose={() => setSeq(false)} />
    </div>
  );
};

// Editable contact card (emails, best email, phones, links) + send actions.
export const CandidateContactPanel = ({ c, pid, endpoint, recordSource, readOnly, best, campaign }) => {
  const [v, setV] = useState(() => contactOf(c));
  const [msg, setMsg] = useState(null);
  useEffect(() => { setV(contactOf(c)); }, [c]);
  const email = best || c.best_email || v.emails[0]?.value || c.email || "";
  return (
    <div className="space-y-2.5" data-testid="cand-contact-panel">
      <TalentContact id={c.id} endpoint={endpoint} v={v} onChange={setV} onMessage={setMsg} readOnly={readOnly} />
      {!readOnly && <CandidateActions c={c} pid={pid} email={email} recordSource={recordSource} campaign={campaign} onEmail={(to) => setMsg({ channel: "email", to })} />}
      <MessageDialog c={c} pid={pid} msg={msg} onClose={() => setMsg(null)} />
    </div>
  );
};
